// client/src/components/pages/CustomSummaries/TemplateLibraryModal.js
import React, { useState, useEffect } from 'react';
import { FaTimes, FaSearch, FaTable, FaStar, FaPlus, FaCode } from 'react-icons/fa';
import api from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';

const TemplateLibraryModal = ({ onClose, onTemplateSelected }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showBuiltInOnly, setShowBuiltInOnly] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [searchTerm, showBuiltInOnly]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        search: searchTerm || undefined,
        isBuiltIn: showBuiltInOnly ? 'true' : undefined,
      };

      // Remove empty params
      Object.keys(params).forEach((key) => {
        if (!params[key]) delete params[key];
      });

      const response = await api.customSummaries.templates.getAll(params);
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.response?.data?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (template) => {
    if (onTemplateSelected) {
      onTemplateSelected(template);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FaTable className="text-primary-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Template Library</h2>
              <p className="text-gray-600">Choose from pre-built summary templates</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={showBuiltInOnly}
                  onChange={(e) => setShowBuiltInOnly(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Built-in only</span>
              </label>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="text-center py-12">
              <LoadingSpinner size="lg" />
              <p className="text-gray-600 mt-4">Loading templates...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                <p className="text-red-600">{error}</p>
                <button
                  onClick={fetchTemplates}
                  className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FaTable className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
              <p className="text-gray-600">
                {searchTerm
                  ? 'Try adjusting your search criteria.'
                  : 'No templates are available at the moment.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <div
                  key={template.template_id}
                  className="bg-surface border border-gray-200 rounded-lg hover:shadow-md transition-shadow duration-200"
                >
                  {/* Template Header */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                          <FaTable className="text-gray-500" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {template.template_name}
                          </h3>
                          {template.is_built_in && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                              <FaStar className="mr-1" size={10} />
                              Built-in
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Template Content */}
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {template.description}
                    </p>

                    {/* SQL Preview */}
                    {template.query_template && (
                      <div className="mb-4">
                        <div className="flex items-center text-xs text-gray-500 mb-1">
                          <FaCode className="mr-1" />
                          SQL Preview
                        </div>
                        <div className="bg-gray-50 rounded p-2 text-xs font-mono text-gray-700 max-h-20 overflow-hidden">
                          {template.query_template.substring(0, 150)}
                          {template.query_template.length > 150 && '...'}
                        </div>
                      </div>
                    )}

                    {/* Parameters */}
                    {template.parameter_schema?.parameters?.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs text-gray-500 mb-1">Parameters</div>
                        <div className="flex flex-wrap gap-1">
                          {template.parameter_schema.parameters.map((param, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs"
                            >
                              @{param.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Template Actions */}
                  <div className="px-4 pb-4">
                    <button
                      onClick={() => handleUseTemplate(template)}
                      className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors duration-200"
                    >
                      <FaPlus className="mr-2" />
                      Use Template
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            {templates.length} template{templates.length !== 1 ? 's' : ''} found
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateLibraryModal;
